import { CloseX } from '@elliemae/ds-icons';
import {
  describe,
  useGetGlobalAttributes,
  useGetXstyledProps,
  useMemoMergePropsWithDefault,
  useOwnerProps,
} from '@elliemae/ds-props-helpers';
import React, { useCallback, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { uid } from 'uid';
import { useGetLayoutMode } from '@elliemae/ds-system';
import { DSBannerContent } from './DSBannerContent.js';
import { DSBannerDatatestid, DSBannerName } from './exported-related/index.js';
import type { DSBannerT } from './react-desc-prop-types.js';
import { defaultProps, DSBannerPropTypesSchema } from './react-desc-prop-types.js';
import {
  StyledLiveRegionPortal,
  StyledBannerContainer,
  StyledBannerLayout,
  StyledCloseButton,
  StyledInnerContainer,
} from './styles.js';

const outOfTheBoxCols = ['auto', 'fit-content(50%)', '1fr'];
const outOfTheBoxColsSmallLayout = ['auto', '1fr'];
const colsWithCustomBody = ['1fr', 'auto'];
const typeMap = {
  alert: undefined,
  polite: 'polite',
  assertive: 'assertive',
  none: 'off',
} as const;

const getIsLiveRegion = (type: string) => type === 'polite' || type === 'assertive';

const LiveRegionCorrectlyImplemented = ({
  id,
  innerRef,
  announcementType,
  ownerPropsConfig,
}: {
  id: string;
  innerRef: React.Ref<HTMLDivElement>;
  announcementType: 'alert' | 'polite' | 'assertive' | 'none';
  ownerPropsConfig: Record<string, unknown>;
}) =>
  ReactDOM.createPortal(
    <StyledLiveRegionPortal
      id={id}
      innerRef={innerRef}
      aria-atomic="true"
      // off is never going to be printed because this portal is not printed when announcement is none
      // the off is just a quick hack to circumvent typescript typechecks in the static building
      aria-live={typeMap[announcementType]}
      data-testid={DSBannerDatatestid.LIVE_REGION_PORTAL}
      {...ownerPropsConfig}
    />,
    document.body,
  );

const DSBanner: React.ComponentType<DSBannerT.Props> = (props) => {
  const propsWithDefaults = useMemoMergePropsWithDefault<DSBannerT.InternalProps>(props, defaultProps);

  const {
    type,
    isOpen,
    onClose,
    label,
    body,
    closeBtnRef,
    CustomBody,
    actionLink,
    showCloseButton,
    containerProps,
    actionRef,
    withLiveRegionAnnouncement,
    isPageNotice,
    ...rest
  } = propsWithDefaults;
  // eslint-disable-next-line no-unused-vars
  const { height: globalHeight, ...globalAttrs } = useGetGlobalAttributes(rest);
  const ownerPropsConfig = useOwnerProps(propsWithDefaults, { ...props });
  const xstyledAttrs = useGetXstyledProps(rest);

  const layoutMode = useGetLayoutMode();
  const isSmall = layoutMode === 's' || layoutMode === 'xs';
  const innerContainerCols = useMemo(() => {
    if (CustomBody) return colsWithCustomBody;
    return isSmall ? outOfTheBoxColsSmallLayout : outOfTheBoxCols;
  }, [CustomBody, isSmall]);

  const handleActionRefForContainer = useCallback<React.RefCallback<HTMLDivElement>>(
    (containerNode) => {
      if (containerNode && actionRef && actionRef.current) {
        actionRef.current.focusOnWrapper = () => {
          if (containerNode) {
            setTimeout(() => {
              containerNode?.focus();
            }, 300);
          }
        };
      }
    },
    [actionRef],
  );

  const generatedContainerId = useMemo(() => `ds-banner-${uid(5)}`, []);

  const handleOnKeyDownToClose = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    },
    [onClose],
  );

  const liveRegionRef = React.useRef<HTMLDivElement>(null);
  const isOpenRef = React.useRef(isOpen);
  isOpenRef.current = isOpen;
  const textToAnnounceRef = React.useRef(`${label} ${body}`);
  textToAnnounceRef.current = `${label} ${body}`;
  const read = React.useCallback(() => {
    if (liveRegionRef.current) {
      if (isOpenRef.current) liveRegionRef.current.textContent = textToAnnounceRef.current;
      else liveRegionRef.current.textContent = '';
    }
  }, []);

  useEffect(() => {
    if (isOpen && getIsLiveRegion(withLiveRegionAnnouncement)) read();
  }, [isOpen, withLiveRegionAnnouncement, read]);

  // this used to "try" to animate the banner closing
  //   in reality the closing animation has never actually worked (the banner just disappears, due to using "&&" instead of "||" in the logic)
  //   given that it has never worked, and the complexity it adds to the code, we are officially removing the closing animation for now
  //   if there is a future need for it, we can re-visit adding it back in.
  if (!isOpen)
    return getIsLiveRegion(withLiveRegionAnnouncement) ? (
      <LiveRegionCorrectlyImplemented
        id={`${generatedContainerId}-live-region`}
        innerRef={liveRegionRef}
        announcementType={withLiveRegionAnnouncement}
        ownerPropsConfig={ownerPropsConfig}
      />
    ) : null;

  return (
    <StyledBannerContainer
      onKeyDown={handleOnKeyDownToClose}
      data-testid={DSBannerDatatestid.CONTAINER}
      innerRef={handleActionRefForContainer}
      tabIndex={-1}
      role={isPageNotice ? 'region' : undefined}
      {...containerProps}
      {...globalAttrs}
      {...xstyledAttrs}
      {...ownerPropsConfig}
    >
      <StyledBannerLayout {...ownerPropsConfig} $type={type}>
        <StyledInnerContainer
          cols={innerContainerCols}
          data-type={type}
          data-testid={DSBannerDatatestid.INNER_CONTAINER}
          role={withLiveRegionAnnouncement === 'alert' ? 'alert' : undefined}
          id={generatedContainerId}
          {...ownerPropsConfig}
        >
          {!CustomBody ? <DSBannerContent propsWithDefaults={propsWithDefaults} /> : <CustomBody />}
        </StyledInnerContainer>
        {!CustomBody && showCloseButton ? (
          <StyledCloseButton
            data-testid={DSBannerDatatestid.CLOSE_BUTTON}
            buttonType="icon"
            innerRef={closeBtnRef}
            onClick={onClose}
            aria-label="Visually dismiss the banner"
            aria-describedby={generatedContainerId}
            {...ownerPropsConfig}
          >
            <CloseX width={12} height={12} />
          </StyledCloseButton>
        ) : null}
      </StyledBannerLayout>
      {getIsLiveRegion(withLiveRegionAnnouncement) ? (
        <LiveRegionCorrectlyImplemented
          id={`${generatedContainerId}-live-region`}
          innerRef={liveRegionRef}
          announcementType={withLiveRegionAnnouncement}
          ownerPropsConfig={ownerPropsConfig}
        />
      ) : null}
    </StyledBannerContainer>
  );
};

DSBanner.displayName = DSBannerName;
const DSBannerWithSchema = describe(DSBanner);
DSBannerWithSchema.propTypes = DSBannerPropTypesSchema;

export default DSBanner;
export { DSBanner, DSBannerWithSchema };
